import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'
import {
  CheckCircle,
  ArrowBack,
  Star,
  ExpandMore,
  Bolt
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

export default function ZapierIntegrationGuide() {
  const navigate = useNavigate()

  const ticketCreatedPayload = `{
  "event": "ticket.created",
  "timestamp": "2025-01-15T10:30:00Z",
  "data": {
    "ticket": {
      "id": "550e8400-e29b-41d4-a716-446655440000",
      "number": "TKT-2025-001234",
      "subject": "Login Issue",
      "description": "Unable to access the system",
      "priority": "High",
      "status": "Open",
      "created_at": "2025-01-15T10:30:00Z",
      "updated_at": "2025-01-15T10:30:00Z",
      "url": "https://helpdesk.taylindigital.com/agent/tickets/550e8400..."
    },
    "customer": {
      "id": "customer-123",
      "company_name": "Acme Corporation"
    },
    "submitter": {
      "id": "user-456",
      "name": "John Doe",
      "email": "john.doe@acme.com"
    }
  }
}`

  const ticketAssignedPayload = `{
  "event": "ticket.assigned",
  "timestamp": "2025-01-15T10:35:00Z",
  "data": {
    "ticket": {
      "id": "550e8400-e29b-41d4-a716-446655440000",
      "number": "TKT-2025-001234",
      "subject": "Login Issue",
      "priority": "High",
      "status": "Open",
      "url": "https://helpdesk.taylindigital.com/agent/tickets/550e8400..."
    },
    "customer": {
      "id": "customer-123",
      "company_name": "Acme Corporation"
    },
    "assignee": {
      "id": "agent-789",
      "name": "Jane Smith",
      "email": "jane.smith@support.com"
    }
  }
}`

  const useCases = [
    {
      title: 'Create Tasks in Project Management Tools',
      description: 'Automatically create tasks in Asana, Trello, Monday.com, or Jira when high-priority tickets are created',
      icon: <CheckCircle color="success" />
    },
    {
      title: 'Send Notifications to Slack',
      description: 'Post ticket updates to specific Slack channels based on priority, category, or customer',
      icon: <CheckCircle color="success" />
    },
    {
      title: 'Log Tickets in Google Sheets',
      description: 'Keep a running log of all tickets in a Google Sheet for reporting and analysis',
      icon: <CheckCircle color="success" />
    },
    {
      title: 'Send SMS Alerts',
      description: 'Use Twilio to send SMS notifications to on-call staff when critical tickets are created',
      icon: <CheckCircle color="success" />
    },
    {
      title: 'Create Calendar Events',
      description: 'Add ticket due dates to Google Calendar or Outlook to track deadlines',
      icon: <CheckCircle color="success" />
    },
    {
      title: 'Update CRM Records',
      description: 'Sync ticket activity to Salesforce, HubSpot, or Zoho CRM customer records',
      icon: <CheckCircle color="success" />
    }
  ]

  const events = [
    {
      event: 'ticket.created',
      name: 'Ticket Created',
      description: 'Triggers when a new support ticket is submitted',
      payload: ticketCreatedPayload
    },
    {
      event: 'ticket.assigned',
      name: 'Ticket Assigned',
      description: 'Triggers when a ticket is assigned to an agent',
      payload: ticketAssignedPayload
    },
    {
      event: 'ticket.resolved',
      name: 'Ticket Resolved',
      description: 'Triggers when a ticket is marked as resolved',
      payload: '{ "event": "ticket.resolved", ... }'
    },
    {
      event: 'ticket.updated',
      name: 'Ticket Updated',
      description: 'Triggers when a ticket status or priority changes',
      payload: '{ "event": "ticket.updated", ... }'
    },
    {
      event: 'ticket.commented',
      name: 'Comment Added',
      description: 'Triggers when a comment is added to a ticket',
      payload: '{ "event": "ticket.commented", "data": { "comment": {...}, ... } }'
    }
  ]

  const faqs = [
    {
      question: 'Do I need a paid Zapier account?',
      answer: 'You can use Zapier\'s free plan, which includes 100 tasks/month. For higher volume or multi-step Zaps, you\'ll need a paid plan. Check Zapier\'s pricing at zapier.com/pricing'
    },
    {
      question: 'How quickly do Zaps trigger?',
      answer: 'Our webhook sends data instantly to Zapier. On the free plan, Zaps check for new data every 15 minutes. Paid plans offer near-instant triggering (typically 1-2 seconds).'
    },
    {
      question: 'Can I filter which tickets trigger my Zap?',
      answer: 'Yes! You can use Zapier\'s built-in filters to only trigger on specific conditions, like tickets with "High" priority or from a specific customer.'
    },
    {
      question: 'What happens if my Zap is turned off?',
      answer: 'Your helpdesk will continue to function normally. Webhook notifications will be sent but not processed by Zapier. Once you turn your Zap back on, it will only process new events (not historical ones).'
    },
    {
      question: 'Can I create multiple Zaps for different events?',
      answer: 'Absolutely! You can create separate Zaps for each event type (e.g., one for ticket.created, another for ticket.resolved) to trigger different workflows.'
    },
    {
      question: 'Is my webhook URL safe?',
      answer: 'Your webhook URL should be kept private. Anyone with the URL can send data to your Zap. If you suspect it\'s been compromised, you can disconnect and reconnect your integration to get a new URL.'
    },
    {
      question: 'What apps can I connect with Zapier?',
      answer: 'Zapier connects with 6,000+ apps including Slack, Gmail, Google Sheets, Trello, Salesforce, HubSpot, Microsoft Teams, Airtable, and many more. Visit zapier.com/apps to see all options.'
    }
  ]

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/admin/settings')}
        sx={{ mb: 3 }}
      >
        Back to Settings
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Bolt sx={{ fontSize: 40, color: 'warning.main' }} />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 0 }}>
          Zapier Integration Setup Guide
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Connect your helpdesk to 6,000+ apps with Zapier. Automate workflows and send ticket notifications anywhere.
      </Typography>

      {/* What is Zapier */}
      <Card sx={{ mb: 4, bgcolor: 'primary.lighter' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            What is Zapier?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Zapier is an automation platform that connects your helpdesk with thousands of other apps—without any coding.
            When something happens in your helpdesk (like a new ticket), Zapier can automatically trigger actions in other apps
            (like creating a Trello card, sending a Slack message, or logging data to Google Sheets).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Think of it as a digital assistant that watches for ticket events and carries out your instructions across all your tools.
          </Typography>
        </CardContent>
      </Card>

      {/* Popular Use Cases */}
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Popular Use Cases
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Here are some powerful ways teams use this integration:
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <List>
            {useCases.map((useCase, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {useCase.icon}
                </ListItemIcon>
                <ListItemText
                  primary={useCase.title}
                  secondary={useCase.description}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          What you'll need:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircle fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText primary="A free or paid Zapier account (sign up at zapier.com)" />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircle fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText primary="Admin access to your Helpdesk settings" />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircle fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText primary="10 minutes to complete setup" />
          </ListItem>
        </List>
      </Alert>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Setup Instructions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Follow these step-by-step instructions to connect your helpdesk to Zapier:
      </Typography>

      <Stepper orientation="vertical">
        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Create a New Zap in Zapier</Typography>
          </StepLabel>
          <StepContent>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. Log in to Zapier at zapier.com"
                  secondary="Create a free account if you don't have one"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. Click 'Create Zap' in the top left"
                  secondary="Or click '+ Create' → 'Zap' from your dashboard"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Give your Zap a descriptive name"
                  secondary="Example: 'Helpdesk → Slack Notifications' or 'High Priority Tickets → SMS Alert'"
                />
              </ListItem>
            </List>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Set Up the Trigger (Webhooks by Zapier)</Typography>
          </StepLabel>
          <StepContent>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. In the trigger step, search for 'Webhooks by Zapier'"
                  secondary="Select this app - it's Zapier's built-in webhook receiver"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. Choose event: 'Catch Hook'"
                  secondary="This will create a webhook URL that listens for incoming data"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Click 'Continue'"
                  secondary="You'll see a webhook URL appear - copy this URL"
                />
              </ListItem>
            </List>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Important: Keep this URL private!
              </Typography>
              <Typography variant="body2">
                This URL must start with <code>https://hooks.zapier.com</code>. Anyone with this URL can send data to your Zap.
              </Typography>
            </Alert>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Configure Your Helpdesk Integration</Typography>
          </StepLabel>
          <StepContent>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. Go to Admin Settings → Integrations in your helpdesk"
                  secondary="You need Admin access to configure integrations"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. Find the Zapier Integration section"
                  secondary="It's on the same page as Microsoft Teams integration"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Toggle 'Enable Zapier Notifications' to ON"
                  secondary="This activates the integration"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="4. Paste your Zapier webhook URL"
                  secondary="The URL you copied from step 2 (should start with https://hooks.zapier.com)"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="5. Select which events to send to Zapier"
                  secondary="Choose ticket.created, ticket.assigned, etc. based on what you want to automate"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="6. Click 'Test Connection'"
                  secondary="This sends a test event to Zapier to verify everything works"
                />
              </ListItem>
            </List>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Test Your Trigger in Zapier</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Back in Zapier, after you clicked "Test Connection" in your helpdesk:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. Click 'Test trigger' in Zapier"
                  secondary="Zapier will look for the test data we just sent"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. You should see sample ticket data appear"
                  secondary="This confirms the connection is working"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Click 'Continue with selected record'"
                  secondary="This tells Zapier what data structure to expect"
                />
              </ListItem>
            </List>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                ✓ Great! Your trigger is set up. Now let's tell Zapier what to do with this data.
              </Typography>
            </Box>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Add an Action</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose what should happen when a ticket event occurs:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Example 1: Send to Slack"
                  secondary="Search for 'Slack' → Choose 'Send Channel Message' → Map ticket.subject to the message content"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Example 2: Create Google Sheets row"
                  secondary="Search for 'Google Sheets' → Choose 'Create Spreadsheet Row' → Map ticket fields to columns"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Example 3: Create Trello card"
                  secondary="Search for 'Trello' → Choose 'Create Card' → Use ticket.subject as card name, ticket.description as description"
                />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Pro Tip:</strong> Use Zapier's "Filter" step to only trigger actions for specific conditions,
                like only high-priority tickets or tickets from a specific customer.
              </Typography>
            </Alert>
          </StepContent>
        </Step>

        <Step active expanded>
          <StepLabel>
            <Typography variant="h6">Test and Activate Your Zap</Typography>
          </StepLabel>
          <StepContent>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. Click 'Test step' in Zapier"
                  secondary="This will send the test data through your action to verify it works"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. Check the destination app (Slack, Trello, etc.)"
                  secondary="Verify that the test data appeared correctly"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="3. Click 'Publish' or toggle your Zap ON"
                  secondary="Your automation is now live!"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="4. Create a test ticket in your helpdesk"
                  secondary="Verify that it triggers your Zap in real-time"
                />
              </ListItem>
            </List>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Star color="success" />
                Success! Your helpdesk is now connected to Zapier.
              </Typography>
            </Box>
          </StepContent>
        </Step>
      </Stepper>

      <Divider sx={{ my: 4 }} />

      {/* Available Events */}
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Available Events and Payload Structure
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your webhook sends different data depending on the event type. Here's what you'll receive:
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Event</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                    {event.event}
                  </Typography>
                </TableCell>
                <TableCell>{event.name}</TableCell>
                <TableCell>{event.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Sample Payloads */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        Sample Payloads
      </Typography>
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            ticket.created Event Payload
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'common.white' }}>
            <pre style={{ margin: 0, overflow: 'auto', fontSize: '12px' }}>
              <code>{ticketCreatedPayload}</code>
            </pre>
          </Paper>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 4 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            ticket.assigned Event Payload
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'common.white' }}>
            <pre style={{ margin: 0, overflow: 'auto', fontSize: '12px' }}>
              <code>{ticketAssignedPayload}</code>
            </pre>
          </Paper>
        </AccordionDetails>
      </Accordion>

      {/* FAQs */}
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Frequently Asked Questions
      </Typography>
      <Box sx={{ mb: 4 }}>
        {faqs.map((faq, index) => (
          <Accordion key={index}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Troubleshooting */}
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Troubleshooting
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Test connection failed
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Make sure your webhook URL starts with https://hooks.zapier.com" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Verify your Zap is turned ON in Zapier" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Check that you copied the entire webhook URL without extra spaces" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Make sure at least one event is selected in your helpdesk settings" />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Zap not triggering
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Verify that the Zapier integration is enabled in your helpdesk settings" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Check the 'Zap History' in Zapier to see if webhook data is being received" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Ensure the event type you want is selected in your helpdesk settings" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• On Zapier's free plan, Zaps check every 15 minutes (not instant). Consider upgrading for instant triggers." />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Missing data in Zap
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Some fields may be empty if not set on the ticket (e.g., assignee if ticket isn't assigned)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Use Zapier's 'Formatter' step to handle empty/null values gracefully" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Check the sample payload examples above to see what data is available for each event" />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Alert severity="info">
        <Typography variant="body2">
          <strong>Need help?</strong> Contact your administrator or reach out to support if you're having trouble setting up the integration.
          You can also visit Zapier's help center at help.zapier.com for general Zapier questions.
        </Typography>
      </Alert>
    </Box>
  )
}
